import { useMemo } from 'react';
import type { LucideIcon } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import FilterDropdownTrigger from './FilterDropdownTrigger';
import { filterItemSelectedClass } from './styles';

export type KpiGroup<Id extends string> = {
  id: string;
  label: string;
  chips: { id: Id; label: string }[];
};

export type KpiGroupsProps<Id extends string> = {
  kpis: Partial<Record<Id, number>>;
  activeIds: string[];
  onGroupSelectionChange: (groupId: string, ids: Id[]) => void;
  iconMap: Record<Id, LucideIcon>;
};

/** Un gruppo di filtri KPI a scelta multipla. */
export default function FilterMultiSelectGroup<Id extends string>({
  group,
  kpis,
  activeIds,
  onGroupSelectionChange,
  iconMap,
}: KpiGroupsProps<Id> & { group: KpiGroup<Id> }) {
  const selectedIds = useMemo(
    () => group.chips.map((chip) => chip.id).filter((id) => activeIds.includes(id)),
    [group.chips, activeIds]
  );

  const toggle = (id: Id, checked: boolean) =>
    onGroupSelectionChange(
      group.id,
      checked ? [...selectedIds, id] : selectedIds.filter((selected) => selected !== id)
    );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <FilterDropdownTrigger
          label={group.label}
          count={selectedIds.length}
          active={selectedIds.length > 0}
        />
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="start"
        sideOffset={6}
        className="flex max-h-64 w-[280px] flex-col gap-1"
      >
        {group.chips.map((chip) => {
          const Icon: LucideIcon = iconMap[chip.id];

          return (
            <DropdownMenuCheckboxItem
              key={chip.id}
              checked={selectedIds.includes(chip.id)}
              onCheckedChange={(checked) => toggle(chip.id, checked === true)}
              className={filterItemSelectedClass}
            >
              <Icon
                className="h-4 w-4 text-placeholder in-data-highlighted:text-current"
                aria-hidden
              />
              <span className="min-w-0 flex-1 font-medium">{chip.label}</span>
              <span className="text-caption tabular-nums text-subtle">{kpis[chip.id] ?? 0}</span>
            </DropdownMenuCheckboxItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
